import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const err = (e) => toast.error(e?.response?.data?.detail || e.message);
const rupees = (p) => `₹${(p / 100).toLocaleString("en-IN")}`;

function Booking({ b, me, reload }) {
  const [pay, setPay] = useState(null);
  const [rating, setRating] = useState(5);
  useEffect(() => { api.get(`/lead-bookings/${b.booking_id}/payment`).then((r) => setPay(r.data)).catch(() => {}); }, [b.booking_id, b.payment?.status]);
  const isCustomer = me === b.customer_id;
  const start = () => api.post(`/lead-bookings/${b.booking_id}/pay`).then((r) => setPay(r.data)).catch(err);
  const simulate = (state) => api.post("/findpay/mock/complete", { intent_id: pay.intent_id, state })
    .then(() => api.get(`/lead-bookings/${b.booking_id}/payment`)).then((r) => setPay(r.data)).catch(err);
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-2 text-sm" data-testid="deal-booking">
      <div className="font-black">Booking {b.booking_id} · {b.provider_name} · {rupees(b.amount_paise)} · {b.status}</div>
      <div data-testid="deal-payment-status">Payment: <b>{pay?.status || "none"}</b>
        {pay?.test_mode && <span className="ms-2 rounded-full bg-amber-200 text-amber-900 px-2 text-[10px] font-black" data-testid="deal-test-mode">TEST MODE · no real money</span>}</div>
      {isCustomer && pay?.status !== "paid" && <button onClick={start} className="h-9 px-4 rounded-full bg-stone-900 text-white font-bold" data-testid="deal-pay-btn">{pay?.status === "pending" ? "Resume payment" : "Pay with FindPay"}</button>}
      {isCustomer && pay?.status === "pending" && pay?.test_mode && (
        <div className="flex gap-2" data-testid="deal-simulate">
          {["SUCCESS", "FAILED", "CANCELLED"].map((s) => <button key={s} onClick={() => simulate(s)} className="h-8 px-3 rounded-full border border-stone-300 text-xs font-bold" data-testid={`deal-sim-${s}`}>Simulate {s.toLowerCase()}</button>)}
        </div>
      )}
      {!isCustomer && b.status === "BOOKED" && <button onClick={() => api.post(`/lead-bookings/${b.booking_id}/complete`).then(reload).catch(err)} className="h-9 px-4 rounded-full bg-emerald-700 text-white font-bold" data-testid="deal-complete-btn">Mark work complete</button>}
      {isCustomer && b.status === "COMPLETED" && !b.review && (
        <div className="flex gap-2 items-center">
          <select value={rating} onChange={(e) => setRating(+e.target.value)} className="h-9 rounded-xl border px-2" data-testid="deal-rating">{[5, 4, 3, 2, 1].map((n) => <option key={n}>{n}</option>)}</select>
          <button onClick={() => api.post(`/lead-bookings/${b.booking_id}/review`, { rating }).then(reload).catch(err)} className="h-9 px-4 rounded-full bg-stone-900 text-white font-bold" data-testid="deal-review-btn">Leave review</button>
        </div>
      )}
      {b.review && <div data-testid="deal-review">Reviewed: {b.review.rating}★</div>}
    </div>
  );
}

export default function LeadDeal() {
  const { leadId } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [amount, setAmount] = useState("");
  const load = useCallback(() => {
    api.get(`/leads/${leadId}/quotes`).then((r) => setD(r.data)).catch(() => setD({ denied: true }));
    api.get("/lead-bookings/mine").then((r) => setBookings(r.data.items.filter((b) => b.lead_id === leadId))).catch(() => {});
  }, [leadId]);
  useEffect(() => { if (user) load(); }, [user, load]);
  if (!user) return <div className="p-6"><Link to="/login" className="underline">Sign in</Link> to view this request.</div>;
  if (!d) return <div className="p-6">Loading…</div>;
  if (d.denied) return <div className="p-6 font-bold" data-testid="deal-denied">You don't have access to this request.</div>;
  const quotes = d.items;
  const isProvider = d.role === "provider";
  return (
    <div className="max-w-xl mx-auto px-4 py-6 space-y-3" data-testid="lead-deal-page">
      <h1 className="text-2xl font-black">Request · quotes</h1>
      <div className="text-xs text-stone-500" data-testid="deal-lead-status">Status: {d.lead_status}</div>
      {quotes.length === 0 && <p className="text-sm text-stone-600">No quotes yet.</p>}
      {quotes.map((q) => (
        <div key={q.quote_id} className="rounded-2xl border border-stone-200 bg-white p-3 text-sm flex items-center gap-2" data-testid={`deal-quote-${q.quote_id}`}>
          <span className="font-bold">{q.provider_name}</span><span>{rupees(q.amount_paise)}</span><span className="text-stone-500">{q.note}</span>
          <span className="ms-auto text-xs uppercase font-black text-stone-500">{q.status}</span>
          {q.status === "offered" && q.provider_id !== user.user_id && (
            <button onClick={() => window.confirm(`Accept ${q.provider_name}'s quote of ${rupees(q.amount_paise)}?`) && api.post(`/leads/${leadId}/quotes/${q.quote_id}/accept`).then(load).catch(err)}
                    className="h-8 px-3 rounded-full bg-emerald-600 text-white font-bold" data-testid={`deal-accept-${q.quote_id}`}>Accept</button>
          )}
        </div>
      ))}
      {isProvider && ["pending", "sent", "responded", "quoted"].includes(d.lead_status) && !quotes.some((q) => q.status === "offered") && (
        <div className="flex gap-2" data-testid="deal-quote-form">
          <input type="number" min="1" className="h-10 rounded-xl border px-3 text-sm flex-1" placeholder="Your price (₹)" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="deal-quote-amount" />
          <button disabled={!amount} onClick={() => api.post(`/leads/${leadId}/quotes`, { amount_paise: Math.round(+amount * 100) }).then(load).catch(err)}
                  className="h-10 px-4 rounded-full bg-stone-900 text-white font-bold disabled:opacity-50" data-testid="deal-quote-submit">Send quote</button>
        </div>
      )}
      {!isProvider && ["pending", "sent", "responded", "quoted"].includes(d.lead_status) && (
        <button onClick={() => window.confirm("Withdraw this request? Providers will no longer be able to quote.") && api.post(`/leads/${leadId}/withdraw`).then(load).catch(err)}
                className="text-sm underline text-red-700" data-testid="deal-withdraw">Withdraw request</button>
      )}
      {bookings.map((b) => <Booking key={b.booking_id} b={b} me={user.user_id} reload={load} />)}
      <Link to="/messages" className="block text-sm underline" data-testid="deal-messages-link">Open conversation</Link>
    </div>
  );
}
